import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Image } from "react-bootstrap";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { User } from "../../types/Types";
import "../css/ProfileModal.css";
import { MdOutlineBusiness } from "react-icons/md";
import {
  FaFlag,
  FaMapMarkerAlt,
  FaUniversity,
} from "react-icons/fa";
import CurvedTextOverlay from "../Account/CurvedTextOverlay";
import ReportUserModal from "./ReportUserModal";
import ProfileModalYouthDetails from "./ProfileModalYouthDetails";
import ProfileModalBusinessDetails from "./ProfileModalBusinessDetails";
import {
  IMAGE_SIZES,
  OBJECT_FIT_COVER,
  formatName,
} from "./profileModalHelpers";
import { useAppContext } from "../../Context/AppContext";
import { useMediaQuery } from "react-responsive";
import {
  normalizeMembership,
  getAvatarUrl,
  getProfileMembershipBadgeTheme,
} from "../../utils/connectionUserUtils";

interface ProfileModalProps {
  show: boolean;
  onHide: () => void;
  selectedUser: User | null;
  /** Falls back to translated default when omitted */
  title?: string;
  isConnected?: boolean;
}

function ProfileModal({
  show,
  onHide,
  selectedUser,
  title,
  isConnected,
}: ProfileModalProps) {
  const { t, i18n } = useTranslation();
  const { objectId, user: currentUser } = useAppContext();
  const [showReportModal, setShowReportModal] = useState(false);
  const displayTitle = title ?? t("profile.defaultTitle");
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const imageSize = useMemo(
    () =>
      isDesktop
        ? IMAGE_SIZES.desktop
        : isLgTablet
          ? IMAGE_SIZES.lgTablet
          : isMdTablet
            ? IMAGE_SIZES.mdTablet
            : IMAGE_SIZES.mobile,
    [isDesktop, isLgTablet, isMdTablet]
  );

  const fullName = useMemo(() => {
    if (!selectedUser) return "";
    return `${formatName(selectedUser.firstName || "")} ${formatName(selectedUser.lastName || "")}`.trim();
  }, [selectedUser]);

  const canReportUser = useMemo(() => {
    if (!selectedUser) return false;
    const myObjectId = objectId ?? currentUser?.user?.objectId;
    const myId = currentUser?.user?.id;
    if (myObjectId && selectedUser.objectId && myObjectId === selectedUser.objectId) {
      return false;
    }
    if (myId != null && selectedUser.id != null && myId === selectedUser.id) {
      return false;
    }
    return true;
  }, [currentUser?.user?.id, currentUser?.user?.objectId, objectId, selectedUser]);

  const handleOpenReport = useCallback(() => setShowReportModal(true), []);
  const handleCloseReport = useCallback(() => setShowReportModal(false), []);

  const handleHideProfile = useCallback(() => {
    setShowReportModal(false);
    onHide();
  }, [onHide]);

  // Keep report dialog in sync if parent closes the profile while reporting.
  useEffect(() => {
    if (!show) setShowReportModal(false);
  }, [show]);

  const membershipInfo = useMemo(() => {
    if (!selectedUser) return "";
    if (normalizeMembership(selectedUser.membershipName) === "Youth Membership") {
      return selectedUser.studyInterest
        ? formatName(selectedUser.studyInterest)
        : "";
    }
    return selectedUser.personalPositionCompany
      ? formatName(selectedUser.personalPositionCompany)
      : "";
  }, [selectedUser]);

  /** Circled summary block only — translated when UI language is not English */
  const profileSummaryFields = useMemo(() => {
    if (!selectedUser) return {};
    const fields: Record<string, string> = {};
    if (fullName) fields.fullName = fullName;
    if (membershipInfo) fields.membershipInfo = membershipInfo;
    const sector = selectedUser.businessSectorIndustry?.trim();
    if (sector) fields.sector = sector;
    const city = selectedUser.city?.trim();
    if (city) fields.city = city;
    const businessName = selectedUser.businessName?.trim();
    if (businessName) fields.businessName = businessName;
    const universityCollege = selectedUser.universityCollege?.trim();
    if (universityCollege) fields.universityCollege = universityCollege;
    return fields;
  }, [selectedUser, fullName, membershipInfo]);

  const { data: translatedSummary } = useTranslateDynamicFields(
    profileSummaryFields,
    i18n.resolvedLanguage || i18n.language
  );

  const displayFullName =
    translatedSummary?.fullName?.trim() || fullName;
  const displayMembershipInfo =
    translatedSummary?.membershipInfo?.trim() || membershipInfo;
  const displaySector =
    translatedSummary?.sector?.trim() ||
    selectedUser?.businessSectorIndustry ||
    "";
  const displayCity =
    translatedSummary?.city?.trim() || selectedUser?.city || "";
  const displayBusinessName =
    translatedSummary?.businessName?.trim() || selectedUser?.businessName || "";
  const displayUniversityCollege =
    translatedSummary?.universityCollege?.trim() ||
    selectedUser?.universityCollege ||
    "";

  const profileImageUrl = useMemo(
    () => (selectedUser ? getAvatarUrl(selectedUser) : "") || "/user.png",
    [selectedUser]
  );

  const membershipBadgeTheme = useMemo(
    () =>
      getProfileMembershipBadgeTheme({
        membershipName: selectedUser?.membershipName ?? "",
        // Explorers: segment drives badge when unpaid; paid membershipName still wins in resolver.
        explorerAttendeeType: selectedUser?.explorerAttendeeType ?? "",
        hasPaid: selectedUser?.hasPaid,
        stripeStatus: selectedUser?.stripeStatus,
        userCategory: selectedUser?.userCategory,
      }),
    [
      selectedUser?.membershipName,
      selectedUser?.explorerAttendeeType,
      selectedUser?.hasPaid,
      selectedUser?.stripeStatus,
      selectedUser?.userCategory,
    ]
  );

  const bannerStyle = useMemo(
    () => ({
      backgroundImage: selectedUser?.BannerImage
        ? `url(${selectedUser.BannerImage})`
        : undefined,
      backgroundSize: "cover" as const,
      backgroundPosition: "center" as const,
      backgroundRepeat: "no-repeat" as const,
      width: "100%",
      minHeight: "150px",
      aspectRatio: "3 / 1",
    }),
    [selectedUser?.BannerImage]
  );

  const imageContainerStyle = useMemo(
    () => ({ width: imageSize, height: imageSize }),
    [imageSize]
  );

  const isYouthMembership =
    !!selectedUser &&
    normalizeMembership(selectedUser.membershipName) === "Youth Membership";

  return (
    <>
    <Modal
      size="lg"
      show={show && !showReportModal}
      onHide={handleHideProfile}
      centered
      className="profile-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{displayTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedUser && (
          <>
            <div className="card mx-auto rounded-3 overflow-hidden border-0 shadow-lg">
              <div
                className={`${
                  selectedUser.BannerImage ? "" : "bg-project-gradient"
                } position-relative profile-banner d-flex justify-content-center align-items-center`}
                style={bannerStyle}
              >
                {!selectedUser.BannerImage && (
                  <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <img
                      src="/SMBF-logo-white.png"
                      alt={t("profile.alt.smbfLogo")}
                      width={120}
                      height={120}
                      className="w-25 h-25"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="position-absolute translate-middle image-container">
                  <div
                    className="position-relative w-100 h-100"
                    style={imageContainerStyle}
                  >
                    <Image
                      src={profileImageUrl}
                      roundedCircle
                      alt={t("profile.alt.user")}
                      width={imageSize}
                      height={imageSize}
                      className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement;
                        if (t.src && !t.src.endsWith("/user.png")) t.src = "/user.png";
                      }}
                    />

                    {membershipBadgeTheme && (
                      <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                        <CurvedTextOverlay
                          size={imageSize}
                          stroke={membershipBadgeTheme.stroke}
                          text={membershipBadgeTheme.text}
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mx-3 pt-5 pb-2 flex-grow-1">
                <div className="d-flex justify-content-between align-items-center profile-summary-row">
                  <div className="mt-md-4 mt-lg-5 profile-summary-primary">
                    <h2 className="mb-0">{displayFullName}</h2>
                    {displayMembershipInfo && <span>{displayMembershipInfo}</span>}
                    {displaySector && (
                      <div className="d-flex gap-1">
                        <span className="text-muted">{displaySector}</span>
                      </div>
                    )}
                    {!isConnected && displayCity && (
                      <div className="d-flex gap-1 text-muted align-items-center">
                        <FaMapMarkerAlt /> <span>{displayCity}</span>
                      </div>
                    )}
                  </div>
                  <div className="d-none d-md-block profile-summary-secondary">
                    {isYouthMembership ? (
                      <div className="fw-semibold d-flex gap-1 align-items-center">
                        <FaUniversity size={25} />
                        {displayUniversityCollege || t("profile.notProvidedYet")}
                      </div>
                    ) : (
                      <div className="d-flex gap-3 align-items-center cursor-pointer">
                        {selectedUser.businessLogo ? (
                          <img
                            src={selectedUser.BusinessLogo}
                            className="rounded-circle"
                            width={40}
                            height={40}
                            alt={
                              selectedUser.businessName
                                ? t("profile.alt.businessLogoFor", {
                                    name: selectedUser.businessName,
                                  })
                                : t("profile.alt.businessLogo")
                            }
                            loading="lazy"
                            decoding="async"
                            style={OBJECT_FIT_COVER}
                          />
                        ) : (
                          <MdOutlineBusiness size={30} />
                        )}
                        <div className="fw-semibold">
                          {displayBusinessName || t("profile.notProvidedYet")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="">
                {isYouthMembership ? (
                  <ProfileModalYouthDetails
                    selectedUser={selectedUser}
                    isConnected={isConnected}
                  />
                ) : (
                  <ProfileModalBusinessDetails
                    selectedUser={selectedUser}
                    isConnected={isConnected}
                  />
                )}
              </div>
            </div>
            {canReportUser && (
              <div className="d-flex justify-content-end px-3 pb-3 pt-2 border-top mx-3 mt-2">
                <button
                  type="button"
                  className="btn btn-link text-secondary text-decoration-none p-0 small fw-medium d-inline-flex align-items-center gap-2"
                  onClick={handleOpenReport}
                >
                  <FaFlag className="opacity-75" aria-hidden />
                  {t("profile.report.link")}
                </button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
    <ReportUserModal
      show={show && showReportModal}
      onHide={handleCloseReport}
      reportedUser={selectedUser}
    />
    </>
  );
}

ProfileModal.displayName = "ProfileModal";

export default memo(ProfileModal);
